import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaTimes,
  FaCheck,
  FaExclamationTriangle,
  FaPercent,
  FaDollarSign,
} from "react-icons/fa";
import { useDeleteFee, useFees } from "../../hooks/useSubscribtionDashboard";
import { createPortal } from "react-dom";
import { getErrorMessage } from "../../utils/error";
import { ErrorRetry } from "../../components/common/ErrorRetry";
import {
  Pagination,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";

const FlightFeeList = () => {
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const { mutate: deleteFee, isPending } = useDeleteFee();
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);

  const {
    data: FeesData,
    isLoading: FeesLoading,
    isError: FeesError,
    error: FeesErrorObj,
    refetch: feeRefetch,
  } = useFees({
    limit,
    offset,
  });

  const handlePageChange = (event, page) => {
    setOffset((page - 1) * limit);
  };

  const handleLimitChange = (event) => {
    setLimit(event.target.value);
    setOffset(0); // Reset to first page when limit changes
  };

  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.ceil((FeesData?.data?.meta?.total || 0) / limit);

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    deleteFee(deleteId, {
      onSuccess: () => {
        setShowDeleteModal(false);
        setDeleteId(null);
      },
      onError: (error) => {
        setShowDeleteModal(false);
        setDeleteId(null);
        console.error("Error saving fee:", error);
      },
    });
  };

  const handleView = (config) => {
    setSelectedConfig(config);
    setShowViewModal(true);
  };

  const handleEdit = (id) => {
    navigate(`/subscritionDashboard/service-fees/create?id=${id}`);
  };

  const feeLabels = {
    internationalFlightTicketingFee: "International Flight Ticketing",
    localFlightTicketingFee: "Local Flight Ticketing",
    regionalFlightTicketingFee: "Regional Flight Ticketing",
    apiTicketingFee: "API Ticketing",
    extraFee: "Extra Fee",
    rebookFee: "Rebook",
    voidFee: "Void",
    reIssueFee: "Re-issue",
    nameChangeFee: "Name Change",
    changePassengerDetailFee: "Change Passenger Detail",
    cancelationFee: "Cancellation",
    fullRefundFee: "Full Refund",
    partialRefundFee: "Partial Refund",
    bankTransferFee: "Bank Transfer",
    whiteLabelOnetimeFee: "White Label One-time",
    whiteLabelMonthlySubscriptionFee: "White Label Monthly Subscription",
    invoiceSubscriptionFee: "Invoice Subscription",
    SMSNotificationFee: "SMS Notification",
    whatsappNotificationFee: "WhatsApp Notification",
    cardFee: "Card Fee",
  };

  const getMethodIcon = (method) => {
    return method === "Percentage" ? (
      <FaPercent className="text-blue-500" />
    ) : (
      <FaDollarSign className="text-green-500" />
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 ">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4 sm:mb-0">
          Services Fees
        </h1>
        <button
          onClick={() => navigate("/subscritionDashboard/service-fees/create")}
          className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors shadow-sm"
        >
          <FaPlus className="mr-2" />
          Create New Fee
        </button>
      </div>

      {FeesError ? (
        <ErrorRetry
          message={getErrorMessage(FeesErrorObj)}
          onRetry={() => feeRefetch()}
        />
      ) : (
        <>
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Min Flights
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Max Flights
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Default
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Fee Types
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody className="bg-white divide-y divide-gray-200">
                  {FeesLoading ? (
                    Array.from({ length: 5 }).map((_, index) => (
                      <tr key={index} className="animate-pulse">
                        <td className="px-6 py-4">
                          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-4 bg-gray-200 rounded w-6"></div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <div className="h-4 bg-gray-200 rounded w-16"></div>
                            <div className="h-4 bg-gray-200 rounded w-16"></div>
                            <div className="h-4 bg-gray-200 rounded w-16"></div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex space-x-3">
                            <div className="h-6 w-6 bg-gray-200 rounded-full"></div>
                            <div className="h-6 w-6 bg-gray-200 rounded-full"></div>
                            <div className="h-6 w-6 bg-gray-200 rounded-full"></div>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : FeesData?.data?.data?.length > 0 ? (
                    FeesData.data.data.map((config) => (
                      <tr key={config.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {config.description || "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {config.minimumFlightIssued}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {config.maximumFlightIssued}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {config.isDefault ? (
                            <FaCheck className="text-green-500" />
                          ) : (
                            <FaTimes className="text-red-500" />
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(config)
                              .map(([key, value]) => {
                                if (
                                  typeof value === "object" &&
                                  value?.method
                                ) {
                                  return (
                                    <span
                                      key={key}
                                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                                    >
                                      {getMethodIcon(value.method)}
                                      <span className="ml-1">
                                        {key.replace(/([A-Z])/g, " $1").trim()}
                                      </span>
                                    </span>
                                  );
                                }
                                return null;
                              })
                              .slice(0, 3)}
                            {Object.keys(config).filter(
                              (key) =>
                                typeof config[key] === "object" &&
                                config[key]?.method,
                            ).length > 3 && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                +
                                {Object.keys(config).filter(
                                  (key) =>
                                    typeof config[key] === "object" &&
                                    config[key]?.method,
                                ).length - 3}{" "}
                                more
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-3">
                            <button
                              onClick={() => handleView(config)}
                              className="text-blue-600 hover:text-blue-900 transition-colors"
                              title="View"
                            >
                              <FaEye size={18} />
                            </button>
                            <button
                              onClick={() => handleEdit(config._id)}
                              className="text-green-600 hover:text-green-900 transition-colors"
                              title="Edit"
                            >
                              <FaEdit size={18} />
                            </button>
                            <button
                              onClick={() => handleDelete(config._id)}
                              className="text-red-600 hover:text-red-900 transition-colors"
                              title="Delete"
                            >
                              <FaTrash size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="6"
                        className="px-6 py-12 text-center text-sm text-gray-500"
                      >
                        <div className="flex flex-col items-center">
                          <p className="mb-4">No Fees data found.</p>
                          <button
                            onClick={() =>
                              navigate(
                                "/subscritionDashboard/service-fees/create",
                              )
                            }
                            className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors"
                          >
                            Create your first configuration
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* pagination */}
          <div className="flex justify-between items-center mt-4 px-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Items per page:</span>
              <FormControl size="small" variant="outlined">
                <Select
                  value={limit}
                  onChange={handleLimitChange}
                  disabled={FeesLoading}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </FormControl>
            </div>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              disabled={FeesLoading}
            />
          </div>
        </>
      )}

      {/* View Modal */}
      {showViewModal &&
        selectedConfig &&
        createPortal(
          <div className="fixed inset-0 z-[2000] bg-black/40 flex items-start justify-center overflow-y-auto">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-lg bg-white">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Configuration Details
                </h3>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <FaTimes size={20} />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto p-2">
                {/* Basic Info */}
                <div className="bg-gray-50 p-3 rounded col-span-full">
                  <h4 className="font-medium text-gray-700 mb-2">
                    Basic Information
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    <p className="text-sm">
                      Description: {selectedConfig.description || "-"}
                    </p>
                    <p className="text-sm">
                      Min Flights: {selectedConfig.minimumFlightIssued}
                    </p>
                    <p className="text-sm">
                      Max Flights: {selectedConfig.maximumFlightIssued}
                    </p>
                    <p className="text-sm">
                      Default: {selectedConfig.isDefault ? "Yes" : "No"}
                    </p>
                  </div>
                </div>

                {/* Fee Details */}
                {Object.entries(selectedConfig).map(([key, value]) => {
                  if (
                    key === "id" ||
                    key === "description" ||
                    key === "minimumFlightIssued" ||
                    key === "maximumFlightIssued" ||
                    key === "isDefault" ||
                    key === "createdAt"
                  )
                    return null;

                  if (typeof value === "object" && value !== null) {
                    return (
                      <div key={key} className="bg-gray-50 p-3 rounded">
                        <p className="text-sm font-medium text-gray-700 mb-2">
                          {feeLabels[key] || key}
                        </p>
                        <div className="space-y-1">
                          <div className="flex items-center text-sm">
                            {value.method === "Percentage" ? (
                              <FaPercent
                                className="text-blue-500 mr-1"
                                size={12}
                              />
                            ) : (
                              <FaDollarSign
                                className="text-green-500 mr-1"
                                size={12}
                              />
                            )}
                            <span>Method: {value.method}</span>
                          </div>
                          <p className="text-sm">Value: {value.value}</p>

                          {/* Show min/max charge for Percentage method or for cardFee */}
                          {(value.method === "Percentage" ||
                            key === "cardFee") && (
                            <>
                              {value.minCharge !== undefined && (
                                <p className="text-sm">
                                  Min Charge: {value.minCharge}
                                </p>
                              )}
                              {value.maxCharge !== undefined && (
                                <p className="text-sm">
                                  Max Charge: {value.maxCharge}
                                </p>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })}
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setShowViewModal(false)}
                  className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal &&
        createPortal(
          <div className="fixed inset-0 z-[2000] bg-black/40 flex items-center justify-center">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3 text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                  <FaExclamationTriangle className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-2">
                  Delete Configuration
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Are you sure you want to delete this configuration? This
                  action cannot be undone.
                </p>
                <div className="flex justify-center gap-3">
                  <button
                    onClick={confirmDelete}
                    disabled={isPending}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                  >
                    {isPending ? "Deleting..." : "Delete"}
                  </button>
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default FlightFeeList;
